import { useMemo } from 'react';
import type { Service, ServiceCategory, ServicePricing } from 'types/services';

interface Props {
  services: ServicePricing;
}

interface State {
  prices: ServicePricing;
}

export default function usePricing({ services }: Props): State {
  const minPrices: ServicePricing = useMemo(() => {
    const minPriceMap: Map<string, Service> = new Map();
    const serviceCategories: ServiceCategory[] = [];

    const priceLoop = (serviceCategory: ServiceCategory): void => {
      if (
        !serviceCategories.some(
          existingCategory =>
            existingCategory.categoryLabel === serviceCategory.categoryLabel,
        )
      ) {
        serviceCategories.push(serviceCategory);
      }

      serviceCategory.services.forEach(service => {
        if (service._type === 'service') {
          const current = minPriceMap.get(service.label);
          if (typeof current === 'undefined') {
            minPriceMap.set(service.label, service);
          } else {
            // Check min
            minPriceMap.set(service.label, {
              ...current,
              price: Math.min(current.price, service.price),
            });
          }
        } else {
          priceLoop(service);
        }
      });
    };

    services.forEach(priceLoop);

    const constructPrices = (
      service: Service | ServiceCategory,
    ): Service | ServiceCategory => {
      if (service._type === 'serviceCategory') {
        return {
          ...service,
          services: service.services.map(constructPrices),
        };
      } else {
        return minPriceMap.get(service.label) ?? service;
      }
    };

    return serviceCategories.map(serviceCategory => {
      return {
        ...serviceCategory,
        services: serviceCategory.services.map(constructPrices),
      };
    });
  }, [services]);

  return {
    prices: minPrices,
  };
}
